import React, { Fragment, useMemo, useState, useEffect } from 'react';
import StickyBox from 'react-sticky-box';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Breadcrumb from '../Breadcrumb';
import Sidebar from '../Sidebar';
import {
  GED_EVENTS_LOGS,
  GED_EVENTS_LOGS_EXPORT,
  GET_ALL_USERS,
  PLAN_FOLDERS,
  PLAN_META_LIST,
} from '../../auth/urlParameters';
import { ToastConfirm, ToastError } from '../../../ui/AlertesUi';
import { SimpleSpinner } from '../../../ui/Spinner';
import './ArchiveStyles.css';

const tabs = [
  { key: 'consultations', label: 'Consultations', icon: 'ri-eye-line' },
  { key: 'downloads', label: 'Téléchargements', icon: 'ri-download-2-line' },
  { key: 'modifications', label: 'Modifications', icon: 'ri-edit-2-line' },
  { key: 'suppression', label: 'Suppressions', icon: 'ri-delete-bin-6-line' },
];

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  return value.toString();
};

const ArchiveLogs = () => {
  const session = useSelector((state) => state.sessionAuth);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('consultations');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [plans, setPlans] = useState([]);
  const [folders, setFolders] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [userOptions, setUserOptions] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    userId: '',
    planId: '',
    folderId: '',
  });

  const authHeaders = useMemo(
    () => ({ headers: { Authorization: 'Bearer ' + session.token } }),
    [session.token]
  );

  useEffect(() => {
    setPage(1);
  }, [activeTab, filters]);

  useEffect(() => {
    let mounted = true;
    const loadFilters = async () => {
      try {
        const [plansRes, foldersRes] = await Promise.all([
          axios.get(PLAN_META_LIST(), authHeaders),
          axios.get(PLAN_FOLDERS(), authHeaders),
        ]);
        if (!mounted) return;
        setPlans(plansRes.data?.plans || []);
        const tree = foldersRes.data?.folders || [];
        const flat = [];
        const walk = (nodes, prefix = '') => {
          nodes.forEach((node) => {
            const label = `${prefix}${node.reference ? `${node.reference} - ` : ''}${node.title}`;
            flat.push({ id: node.id, label });
            if (node.children && node.children.length > 0) {
              walk(node.children, `${label} / `);
            }
          });
        };
        walk(tree);
        setFolders(flat);
      } catch (err) {
        setPlans([]);
        setFolders([]);
      }
    };

    loadFilters();
    return () => {
      mounted = false;
    };
  }, [authHeaders]);

  useEffect(() => {
    let mounted = true;
    if (!userSearch.trim()) {
      setUserOptions([]);
      setUserDropdownOpen(false);
      return () => {
        mounted = false;
      };
    }
    setUserLoading(true);
    const timer = setTimeout(() => {
      axios
        .get(GET_ALL_USERS(1, userSearch.trim()), authHeaders)
        .then((res) => {
          if (!mounted) return;
          const users = Array.isArray(res.data) ? res.data : [];
          const options = users.map((account) => {
            const profile = account.profile || {};
            const fullName = `${profile.nom || ''} ${profile.prenoms || ''}`.trim();
            const label = fullName ? `${fullName} (${account.username || '-'})` : (account.username || '-');
            return { id: account.id, label };
          });
          setUserOptions(options);
          setUserDropdownOpen(true);
        })
        .catch(() => {
          if (!mounted) return;
          setUserOptions([]);
          setUserDropdownOpen(false);
        })
        .finally(() => {
          if (!mounted) return;
          setUserLoading(false);
        });
    }, 300);

    return () => {
      mounted = false;
      clearTimeout(timer);
    };
  }, [authHeaders, userSearch]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    axios
      .get(GED_EVENTS_LOGS(), {
        ...authHeaders,
        params: {
          category: activeTab,
          page,
          perPage,
          dateFrom: filters.dateFrom || undefined,
          dateTo: filters.dateTo || undefined,
          userId: filters.userId || undefined,
          planId: filters.planId || undefined,
          folderId: filters.folderId || undefined,
        },
      })
      .then((res) => {
        if (!isMounted) return;
        setEvents(res.data?.events || []);
        setTotalCount(res.data?.count || 0);
      })
      .catch(() => {
        if (!isMounted) return;
        ToastError('Impossible de charger les logs.');
        setEvents([]);
        setTotalCount(0);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [authHeaders, activeTab, page, perPage, filters]);

  const totalPages = Math.max(1, Math.ceil(totalCount / perPage));
  const pageStart = totalCount === 0 ? 0 : (page - 1) * perPage + 1;
  const pageEnd = Math.min(page * perPage, totalCount);

  const parseFilename = (headers, fallback) => {
    const disposition = headers?.['content-disposition'] || '';
    const match = disposition.match(/filename="(.+?)"/);
    if (match && match[1]) {
      return match[1];
    }
    return fallback || 'journal_logs.csv';
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 10000);
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(GED_EVENTS_LOGS_EXPORT(), {
        ...authHeaders,
        responseType: 'blob',
        params: {
          category: activeTab,
          dateFrom: filters.dateFrom || undefined,
          dateTo: filters.dateTo || undefined,
          userId: filters.userId || undefined,
          planId: filters.planId || undefined,
          folderId: filters.folderId || undefined,
        },
      });
      const fallback = `journal_logs_${activeTab}.csv`;
      const filename = parseFilename(res.headers, fallback);
      downloadBlob(res.data, filename);
      ToastConfirm('Export CSV prêt.');
    } catch (err) {
      ToastError("Impossible d'exporter le journal.");
    }
  };

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSelectUser = (option) => {
    setFilters((prev) => ({ ...prev, userId: option.id }));
    setUserSearch(option.label);
    setUserDropdownOpen(false);
  };

  return (
    <Fragment>
      <Breadcrumb title="Archives" submenu="Journal & logs" />
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-4 col-lg-3 col-xl-3 theiaStickySidebar">
              <StickyBox offsetTop={20} offsetBottom={20}>
                <Sidebar />
              </StickyBox>
            </div>
            <div className="col-md-8 col-lg-9 col-xl-9">
              <div className="archive-page-header">
                <div>
                  <h4 className="htitle">Journal & logs</h4>
                  <p className="text-muted mb-0">
                    Traçabilité complète : qui, quand, pourquoi, quoi.
                  </p>
                </div>
                <div className="archive-toolbar-actions">
                  <button
                    className="btn btn-outline-primary btn-file-action"
                    type="button"
                    onClick={handleExport}
                  >
                    Exporter CSV
                  </button>
                  <button
                    className="btn btn-light btn-uniform-action"
                    type="button"
                    onClick={() => navigate('/apps/in/archives')}
                  >
                    Retour aux archives
                  </button>
                </div>
              </div>

              <div className="archive-content">
                <div className="archive-logs-tabs">
                  <ul className="nav nav-tabs nav-custom" role="tablist">
                    {tabs.map((tab) => (
                      <li className="nav-item" key={tab.key}>
                        <button
                          className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
                          type="button"
                          onClick={() => setActiveTab(tab.key)}
                        >
                          <i className={`${tab.icon} me-1`}></i>
                          {tab.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="archive-logs-filters">
                  <div className="row g-2">
                    <div className="col-md-6 col-xl-2">
                      <label className="form-label">Du</label>
                      <input
                        type="date"
                        className="form-control"
                        value={filters.dateFrom}
                        onChange={(e) => updateFilter('dateFrom', e.target.value)}
                      />
                    </div>
                    <div className="col-md-6 col-xl-2">
                      <label className="form-label">Au</label>
                      <input
                        type="date"
                        className="form-control"
                        value={filters.dateTo}
                        onChange={(e) => updateFilter('dateTo', e.target.value)}
                      />
                    </div>
                    <div className="col-md-6 col-xl-3">
                      <label className="form-label">Utilisateur</label>
                      <div className="archive-logs-user-autocomplete">
                        <input
                          type="text"
                          className="form-control"
                          value={userSearch}
                          onChange={(e) => {
                            setUserSearch(e.target.value);
                            setUserDropdownOpen(true);
                            updateFilter('userId', '');
                          }}
                          onFocus={() => setUserDropdownOpen(true)}
                          placeholder="Rechercher par nom/prénom"
                        />
                        {userDropdownOpen && (userLoading || userOptions.length > 0) && (
                          <div className="archive-logs-user-dropdown">
                            {userLoading ? (
                              <div className="archive-logs-user-item">Chargement...</div>
                            ) : (
                              userOptions.map((option) => (
                                <button
                                  key={option.id}
                                  type="button"
                                  className="archive-logs-user-item"
                                  onClick={() => handleSelectUser(option)}
                                >
                                  {option.label}
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6 col-xl-3">
                      <label className="form-label">Plan</label>
                      <select
                        className="form-select"
                        value={filters.planId}
                        onChange={(e) => updateFilter('planId', e.target.value)}
                      >
                        <option value="">Tous</option>
                        {plans.map((plan) => (
                          <option key={plan.id} value={plan.id}>
                            {plan.code} - {plan.title}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6 col-xl-2">
                      <label className="form-label">Dossier</label>
                      <select
                        className="form-select"
                        value={filters.folderId}
                        onChange={(e) => updateFilter('folderId', e.target.value)}
                      >
                        <option value="">Tous</option>
                        {folders.map((folder) => (
                          <option key={folder.id} value={folder.id}>
                            {folder.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="archive-logs-filters-actions">
                    <button
                      className="btn btn-outline-secondary btn-sm btn-uniform-action"
                      type="button"
                      onClick={() => setFilters({
                        dateFrom: '',
                        dateTo: '',
                        userId: '',
                        planId: '',
                        folderId: '',
                      })}
                    >
                      Réinitialiser
                    </button>
                    <button
                      className="btn btn-outline-secondary btn-sm btn-uniform-action"
                      type="button"
                      onClick={() => {
                        setUserSearch('');
                        setUserOptions([]);
                        setUserDropdownOpen(false);
                        updateFilter('userId', '');
                      }}
                    >
                      Effacer utilisateur
                    </button>
                  </div>
                </div>

                <div className="archive-logs-table">
                  {loading ? (
                    <div className="archive-loading">{SimpleSpinner()}</div>
                  ) : events.length === 0 ? (
                    <div className="archive-empty">Aucun événement trouvé.</div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-striped table-hover align-middle syidocs-data-table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Archive</th>
                            <th>Action</th>
                            <th>Utilisateur</th>
                            <th>Motif</th>
                            <th>Commentaire</th>
                            <th>Détails</th>
                          </tr>
                        </thead>
                        <tbody>
                          {events.map((event) => (
                            <tr key={event.id}>
                              <td>{formatValue(event.eventDate)}</td>
                              <td>
                                <div className="archive-logs-doc">
                                  <div className="archive-logs-doc-title">{event.document?.title || '—'}</div>
                                  <div className="archive-logs-doc-meta">
                                    {event.document?.reference || '—'}
                                  </div>
                                </div>
                              </td>
                              <td>{formatValue(event.label)}</td>
                              <td>{formatValue(event.performedBy?.name || event.performedBy?.username)}</td>
                              <td>{formatValue(event.reason)}</td>
                              <td>{formatValue(event.comment)}</td>
                              <td>{formatValue(event.details)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {!loading && totalCount > 0 && (
                    <div className="archive-pagination">
                      <div className="archive-pagination-info">
                        Affichage {pageStart}-{pageEnd} sur {totalCount}
                      </div>
                      <div className="archive-pagination-controls">
                        <button
                          className="btn btn-light btn-sm"
                          type="button"
                          onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                          disabled={page <= 1}
                        >
                          Précédent
                        </button>
                        <span className="archive-pagination-page">
                          Page {page} / {totalPages}
                        </span>
                        <button
                          className="btn btn-light btn-sm"
                          type="button"
                          onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                          disabled={page >= totalPages}
                        >
                          Suivant
                        </button>
                        <select
                          className="form-select form-select-sm"
                          value={perPage}
                          onChange={(e) => setPerPage(Number(e.target.value))}
                        >
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ArchiveLogs;
